import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import apiClient from "../../../helpers/api-client";
import { PaginationInfo } from "../../../types/general-types";

export type EventBannerResponse = {
	results: {
		id: number;
		title: string;
		link: string;
		buttonText: string;
		institutions: string[];
		roles: string[];
		startDate: Date;
		endDate: Date;
		createdAt: Date;
		updatedAt: Date;
	}[];
} & PaginationInfo;

type FetchEventBannerOptions = {
	text?: string;
	roles?: string[];
	startDate?: Date;
	endDate?: Date;
	page: number;
	size: number;
};

type CreateEventBanner = {
	title: string;
	link: string;
	buttonText: string;
	institutions: string[];
	roles: string[];
	startDate: Date;
	endDate: Date;
};

type UpdateEventBanner = {
	id: number;
	title: string;
	link: string;
	buttonText: string;
	institutions: string[];
	roles: string[];
	startDate: Date;
	endDate: Date;
};

const eventBannerApi = createApi({
	reducerPath: "event-banner",
	baseQuery: async (arg, api, extraOptions) =>
		fetchBaseQuery({
			fetchFn: apiClient.fetch.bind(apiClient),
			baseUrl: `/api/verity/event-banner`,
		})(arg, api, extraOptions),
	tagTypes: ["Banner"],
	endpoints: (builder) => ({
		getBanners: builder.query<EventBannerResponse, FetchEventBannerOptions>({
			query: (args) => {
				const searchParams = new URLSearchParams();
				Object.entries(args).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== "") {
						if (key === "roles" && Array.isArray(value)) {
							searchParams.set(key, JSON.stringify(value));
						} else {
							searchParams.set(key, String(value));
						}
					}
				});

				return `?${searchParams.toString()}`;
			},
			providesTags: (result) =>
				result
					? [...result.results.map(({ id }) => ({ type: "Banner" as const, id })), { type: "Banner", id: "LIST" }]
					: [{ type: "Banner" as const, id: "LIST" }],
		}),
		createBanner: builder.mutation<void, CreateEventBanner>({
			query: (args) => ({
				url: "/",
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(args),
			}),
			invalidatesTags: ["Banner"],
		}),
		updateBanner: builder.mutation<void, UpdateEventBanner>({
			query: ({ id, ...args }) => ({
				url: `/${id}`,
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(args),
			}),
			invalidatesTags: (_result, _error, { id }) => [
				{ type: "Banner", id },
				{ type: "Banner", id: "LIST" },
			],
		}),
		deleteEvent: builder.mutation<void, number>({
			query: (id) => ({
				url: `/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: [{ type: "Banner", id: "LIST" }],
		}),
	}),
});

export default eventBannerApi;
